import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  Row, 
  Col,
  Alert,
  Spinner,
  FormFeedback
} from 'reactstrap';
import { generateInvoice, getInvoicePeriodsByProject, getInvoiceByPeriod } from '../../services/api/invoice-api';
import './InvoiceGenerate.css';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye } from 'react-icons/fa';
import { BsFillInfoCircleFill } from "react-icons/bs";

const InvoiceGenerate = ({ projectId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    publisherId: '',
    publisherName: '',
    publisherEmail: '',
    startDate: '',
    endDate: '',
    notes: '',
    dueDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('last');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [generatedPeriods, setGeneratedPeriods] = useState([]);
  const [customMonth, setCustomMonth] = useState(moment().subtract(1, 'month').format('YYYY-MM'));
  const [existingInvoice, setExistingInvoice] = useState(null);
  const lastMonth = moment().subtract(1, 'months');

  const periods = {
    last: {
      name: 'Last Month',
      start: lastMonth.clone().startOf('month').toDate(),
      end: lastMonth.clone().endOf('month').toDate(),
      due: moment().add(7, 'days').toDate()
    }
  };

  useEffect(() => {
    setSelectedPeriod('last');
    setShowCustomDates(false);
     setPeriodDates('last');
  }, []);

  useEffect(() => {
    if (selectedPeriod === 'custom') {
      setPeriodDates('custom', customMonth);
    }
  }, [customMonth, selectedPeriod]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      fetchInvoicePreviewData(
        moment(formData.startDate).startOf('day').format(),
        moment(formData.endDate).endOf('day').format()
      );
    }
  }, [formData.startDate, formData.endDate]);




  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const response = await getInvoicePeriodsByProject(projectId);
        if (response.data?.periods) {
          setGeneratedPeriods(response.data.periods.map(p => p.invoice_period));
        }
      } catch (err) {
        toast.error('Failed to fetch invoice periods. Please try again.');
        console.error('Failed to fetch invoice periods:', err);
      }
    };

    if (projectId) fetchPeriods();
  }, [projectId]);


  const fetchInvoicePreviewData = async (startDate, endDate) => {
    try {
      const res = await getInvoiceByPeriod(projectId, startDate, endDate); // your new API service
      setExistingInvoice(res.data.invoice || null);
    } catch (err) {
      setExistingInvoice(null); // No invoice found
    }
  };



  const formatDateForInput = (date) => {
    return moment(date).format('YYYY-MM-DD'); // ✅
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const setPeriodDates = (periodKey, selectedMonth) => {
    if (periodKey === 'custom') {
      setShowCustomDates(true);

      const selected = moment(selectedMonth, 'YYYY-MM');
      const start = selected.startOf('month').format('YYYY-MM-DD');
      const end = selected.endOf('month').format('YYYY-MM-DD');
      const defaultDue = moment().add(7, 'days').format('YYYY-MM-DD');

      setFormData(prev => ({
        ...prev,
        startDate: start,
        endDate: end,
        dueDate: defaultDue
      }));

      fetchInvoicePreviewData(
        moment(start).startOf('day').format(),
        moment(end).endOf('day').format()
      );

      return;
    }

    // For presets like 'last'
    setShowCustomDates(false);
    const period = periods[periodKey];

    setFormData(prev => ({
      ...prev,
      startDate: moment(period.start).format('YYYY-MM-DD'),
      endDate: moment(period.end).format('YYYY-MM-DD'),
      dueDate: moment(period.due).format('YYYY-MM-DD')
    }));
  };


  const handlePeriodChange = (periodKey) => {
    setSelectedPeriod(periodKey);
    setPeriodDates(periodKey);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (validation[field]) {
      setValidation(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    setError('');
  };

  const validateForm = () => {
    const errors = {};
    
    if (selectedPeriod === 'custom') {
      if (!formData.startDate) {
        errors.startDate = 'Start date is required';
      }
      
      if (!formData.endDate) {
        errors.endDate = 'End date is required';
      }
      
      if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
        errors.endDate = 'End date must be after start date';
      }
      
      if (!formData.dueDate) {
        errors.dueDate = 'Due date is required';
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isLastMonth =
      moment(formData.startDate).isSame(moment().subtract(1, 'month').startOf('month'), 'day') &&
      moment(formData.endDate).isSame(moment().subtract(1, 'month').endOf('month'), 'day');

    if (isLastMonth && moment().date() < 15) {
      toast.warning("You can only generate last month's invoice after the 15th of this month.");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidation(errors);
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      let startDate, endDate, dueDate;

      if (selectedPeriod === 'custom') {
        startDate = moment(formData.startDate).startOf('day').format(); // ← with time
        endDate = moment(formData.endDate).endOf('day').format();       // ← with time
        dueDate = moment(formData.dueDate).startOf('day').format();     // ← optional
      } else {
        const period = periods[selectedPeriod];
        startDate = moment(period.start).startOf('day').format();
        endDate = moment(period.end).endOf('day').format();
        dueDate = moment(period.due).startOf('day').format();
      }

      const payload = {
        publisherId: formData.publisherId,
        projectId,
        startDate,
        endDate,
        dueDate,
        notes: formData.notes
      };

      console.log('Generating invoice with payload:', payload);

      const res = await generateInvoice(payload);
      const invoiceId = res.data?.invoice?._id;

      const invoiceMonth = moment(startDate).format('MMMM');
      toast.success(`Invoice for ${invoiceMonth} generated successfully!`);

      setTimeout(() => {
        onSuccess(invoiceId);
      }, 1000);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to generate invoice. Please try again.';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const getPreviewContent = () => {
    if (selectedPeriod === 'custom' && (!formData.startDate || !formData.endDate || !formData.dueDate)) {
      return 'Please select custom dates to see preview';
    }
    
    let startDate, endDate, dueDate;
    
    if (selectedPeriod === 'custom') {
      startDate = new Date(formData.startDate);
      endDate = new Date(formData.endDate);
      dueDate = new Date(formData.dueDate);
    } else {
      const period = periods[selectedPeriod];
      startDate = period.start;
      endDate = period.end;
      dueDate = period.due;
    }

    if (existingInvoice) {
      return (
        
          <div>
            <strong>Invoice Number:</strong> {existingInvoice.invoice_number}<br />
            <strong>Invoice Month:</strong> {moment(existingInvoice.start_date).format('MMMM YYYY')}<br />
            <strong>Amount:</strong> ₹{existingInvoice.amount}<br />
            <strong>Generated By:</strong> {existingInvoice.generated_by}<br />
            <strong>Status:</strong> {existingInvoice.status}<br />
            {existingInvoice.status === 'paid' && existingInvoice?.payment && (
              <>
                <strong>Paid On:</strong> {moment(existingInvoice?.payment[0]?.payment_date).format('Do MMM YYYY')}<br />
                <strong>Paid By:</strong> {existingInvoice?.payment[0]?.created_by}<br />
              </>
            )}
          </div>
      );
    }
    
    return (
      <div>
        {/* <strong>Invoice Period:</strong> {selectedPeriod === 'custom' ? 'Custom Dates' : periods[selectedPeriod].name}<br/> */}
        <strong>Service Period:</strong> {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)}<br/>
        {/* <strong>Due Date:</strong> {formatDateForDisplay(dueDate)}<br/> */}
        <strong>Invoice Month:</strong> {endDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
      </div>
    );
  };

  const checkOverlappingPeriods = () => {
    const selectedStart = moment(formData.startDate).startOf('day');
    const selectedEnd = moment(formData.endDate).endOf('day');


    return generatedPeriods.filter(period => {
      const genStart = moment(period.start_date);
      const genEnd = moment(period.end_date);

      // Return true if any overlap
      return selectedStart.isSameOrBefore(genEnd) && selectedEnd.isSameOrAfter(genStart);
    });
  };




  const overlapping = formData.startDate && formData.endDate ? checkOverlappingPeriods() : [];
  const uniqueMonths = [...new Set(overlapping.map(p => moment(p.start_date).format('MMMM')))];


  return (
    <>
    <div className="content">
      <Card className="invoice-form-card shadow">
        <CardHeader className="invoice-form-header text-center">
          <div>
            <h1 className="invoice-form-title">Invoice Generator</h1>
            <p className="invoice-form-subtitle mb-2">Create professional invoices with ease</p>
          </div>
        </CardHeader>
        
        <CardBody className="invoice-form-body">

          <Form onSubmit={handleSubmit}>
            {/* Invoice Period Section */}
            <div className="invoice-section">
              <h2 className="invoice-section-title">
                <i className="nc-icon nc-calendar-60 me-2" />
                Invoice Period
              </h2>
              
              <Row className="period-selector mb-4">
                <Col>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        style={{
                          backgroundColor: selectedPeriod === 'last' ? '#615c56' : 'transparent',
                          color: selectedPeriod === 'last' ? '#fff' : '#615c56',
                          borderColor: '#615c56'
                        }}
                        onClick={() => handlePeriodChange('last')}
                        className="period-btn btn-round"
                      >
                        Last Month
                      </Button>

                      <Button
                        style={{
                          backgroundColor: selectedPeriod === 'custom' ? '#615c56' : 'transparent',
                          color: selectedPeriod === 'custom' ? '#fff' : '#615c56',
                          borderColor: '#615c56'
                        }}
                        onClick={() => handlePeriodChange('custom')}
                        className="period-btn btn-round"
                      >
                        Custom Dates
                      </Button>
                    </div>
                </Col>
              </Row>

              {uniqueMonths.length > 0 && (
                <Alert 
                className="mt-3 custom-dates-hint"
                    style={{
                      backgroundColor: '#FFF3CD', // Light yellow background
                      color: '#856404',           // Dark yellow/brown text
                      borderColor: '#FFEEBA'      // Optional border color
                    }}
                >
                  <i className="nc-icon nc-alert-circle-i me-2" />
                  Invoice for the month of <strong>{uniqueMonths.join(', ')}</strong> has already been generated.
                </Alert>
              )}

              {/* Date Inputs - Only show for custom */}
              {showCustomDates && (
                <Row className="date-inputs">
                  <Col md="3">
                    <FormGroup>
                      <Label for="customMonth">Select Month <span className="text-danger">*</span> </Label>
                      <Input
                        type="month"
                        id="customMonth"
                        value={customMonth}
                        onChange={(e) => {
                          const monthValue = e.target.value;
                          setCustomMonth(monthValue);
                          setPeriodDates('custom', monthValue);
                        }}
                        max={moment().subtract(1, 'month').format('YYYY-MM')} // ⛔ blocks current & future months
                        className="form-control-custom"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="startDate">
                        Start Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        id="startDate"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        invalid={!!validation.startDate}
                        className="form-control-custom"
                        disabled={selectedPeriod === 'custom'}
                      />
                      <FormFeedback>{validation.startDate}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="endDate">
                        End Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        id="endDate"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                        invalid={!!validation.endDate}
                        className="form-control-custom"
                        disabled={selectedPeriod === 'custom'}
                      />
                      <FormFeedback>{validation.endDate}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="dueDate">
                        Due Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        id="dueDate"
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        invalid={!!validation.dueDate}
                        className="form-control-custom"
                      />
                      <FormFeedback>{validation.dueDate}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </div>

            {/* Invoice Preview */}
            <div className="invoice-preview">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="invoice-preview-title">Invoice Preview</div>
                  {existingInvoice && (
                    <div>
                      <BsFillInfoCircleFill
                        size={22}
                        title="View Details"
                        style={{ cursor: 'pointer', color: '#615c56' }}
                        onClick={() => onSuccess && onSuccess(existingInvoice._id)}
                      />
                    </div>
                  )} 
                </div>
              <div className="invoice-preview-content">
                {getPreviewContent()}
              </div>
            </div>

            {/* Additional Information */}
            <div className="invoice-section mt-4">
              <h2 className="invoice-section-title">
                <i className="nc-icon nc-single-copy-04 me-2" />
                Additional Information
              </h2>
              
              <FormGroup>
                <Label for="notes">Notes (Optional)</Label>
                <Input
                  type="textarea"
                  id="notes"
                  rows="4"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Add any additional notes or instructions for this invoice..."
                  className="form-control-custom"
                />
              </FormGroup>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end mt-4">
              <Button 
                color="danger" 
                type="button" 
                onClick={onCancel}
                className="me-3 btn-round"
                disabled={submitting}
              >
                <i className="nc-icon nc-simple-remove me-1" />
                Cancel
              </Button>
              <Button 
                color="primary" 
                type="submit" 
                disabled={submitting || overlapping.length > 0}
                className="btn-round btn-generate"
                  style={{
                    backgroundColor: '#66615b',
                    color: '#fff',
                    borderColor: '##66615b'
                  }}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="nc-icon nc-check-2 me-1" />
                    Generate Invoice
                  </>
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
    <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default InvoiceGenerate;